import React from "react";
import { Box, Chip, Link, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AutoSizer } from "react-virtualized";
import { useSelector } from "react-redux";
import moment from "moment";
import { tl, t } from "../../components/translate";
import List, { EmptyView, ListActions, LoadingView, Menu, MenuItem } from "../../components/OList";
import * as calendarFns from "../../components/Calendar/functions/calendarFunctions";
import { clientFullNameSelector } from "../../reducers/client";
import LabStatusChip from "./LabStatusChip";
import "./LabList.scss";
import classNames from "../../helpers/classNames";
import { tekuLabImportColumns } from "../../components/CsvUploader/csvHelpers";
import CsvUploader from "../../components/CsvUploader/CsvUploader";
import { createlabTestWithPCR } from "../../api/labTest";
import Can from "../Policy/Can";
import hasOwnProperty from "../../helpers/object";
import { RootState } from "../../store";
import { LabTestStatusTypes } from "./LabList";
import { LabObject } from "../../interfaces/LabInterfaces";
import { LabRecordType } from "./index";
import { getCustomerNumber } from "../Client/ClientList";
import { ListLoadingView } from "../../components/List";
import useGetOnGoing from "../../hooks/useGetOnGoing";
import { Type } from "../../actions/labTest";

function downloadLabUploadSample() {
  window.open("https://docs.google.com/spreadsheets/d/1ugbK2sh8F74BXBFwBfQyu1MIL7z8vVkG", "_blank");
}

interface ListTabProps {
  labTestsFiltered: any;
  selectedItem: any;
  navigateTo: (url: string) => void;
  onMultiSelect: any;
  multiSelectContextHeader: any;
  handleGetLabsTestRecords: any;
  currentTab: string;
  showPageControl: boolean;
  labRecordType: LabRecordType;
}

export const getTurnAroundTime = (labObj: LabObject | Partial<LabObject>): string => {
  const startTime = labObj.labCreatedTime || labObj.created_at;
  let endTime;

  if (
    [LabTestStatusTypes.RESULT_READY, LabTestStatusTypes.DISPATCHED].includes(
      labObj.status as LabTestStatusTypes
    )
  ) {
    endTime = labObj.resultDate;
  } else if ([LabTestStatusTypes.CANCELED].includes(labObj.status as LabTestStatusTypes)) {
    endTime = labObj.updated_at;
  } else {
    endTime = new Date();
  }

  const diffInMinutes = moment(endTime).diff(startTime, "minutes");
  if (diffInMinutes < 60) return `${diffInMinutes} mins`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours > 24) return `${Math.floor(diffInHours / 24)} day ${diffInHours % 24} hrs`;

  return `${diffInHours} hrs`;
};

function EmergencyChip() {
  return (
    <Chip
      size="small"
      label="Emergency"
      sx={{
        height: 20,
        fontSize: "0.7rem",
        color: "#c62828",
        backgroundColor: "#ffcccc",
        border: "1px solid #e57373"
      }}
    />
  );
}

function MobileLabCard({ item, isSelected, onClick }) {
  const {
    results,
    related,
    client,
    customer,
    created_at: createdAt,
    status,
    id,
    labTestNumber,
    emergencyId
  } = item;

  const allTests = [...(results?.data || [])].filter(Boolean);

  if (related?.length > 0) {
    related.forEach((rl) => {
      rl.results?.data?.forEach((resd) => allTests.push(resd));
    });
  }

  const filtered = allTests.filter((v, i, a) => a.findIndex((x) => x.id === v.id) === i);

  const testNames = filtered
    .slice(0, 2)
    .map((lt) => lt.name)
    .join(", ");

  const extraCount = filtered.length > 2 ? ` +${filtered.length - 2}` : "";

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: isSelected ? "action.selected" : "background.paper",
        cursor: client?.active ? "pointer" : "default",
        "&:active": { backgroundColor: "action.hover" },
        gap: 1
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 0.25 }}>
          <Typography
            sx={{ fontSize: "11px", color: "text.disabled", fontWeight: 600, flexShrink: 0 }}
          >
            {labTestNumber || id}
          </Typography>

          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {clientFullNameSelector(client)?.toUpperCase()}
            {!client?.active ? " (DELETED)" : ""}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {testNames}
            {extraCount && (
              <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
                {extraCount}
              </Box>
            )}
          </Typography>

          {emergencyId && <EmergencyChip />}
        </Box>

        <Typography sx={{ fontSize: "11px", color: "text.disabled", mt: 0.25 }}>
          {calendarFns.bsFullDate(createdAt)}
          {customer?.customerNumber ? ` - ${getCustomerNumber(customer.customerNumber)}` : ""}
        </Typography>
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <LabStatusChip status={status?.toLowerCase()} />
      </Box>
    </Box>
  );
}

function ListTab({
  labTestsFiltered,
  selectedItem,
  navigateTo,
  onMultiSelect = null,
  multiSelectContextHeader = null,
  handleGetLabsTestRecords,
  currentTab,
  showPageControl,
  labRecordType
}: ListTabProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentRCId = useSelector((state: any) => state.userContext.resourceCentreId);
  const isLoading = useGetOnGoing(Type.GET_LAB_TEST_RECORDS);

  const useCustomLabTestNumber = useSelector((state: RootState) =>
    Boolean(state.resources?.resourceCentres[0]?.labSettings?.useCustomLabTestNumber)
  );

  const height = showPageControl ? "calc(100vh - 168px)" : "calc(100vh - 128px)";

  const handleRowClick = (item) => {
    if (!item.client?.active) return;

    const isAlreadySelected = selectedItem?.id === item.id;

    if (isAlreadySelected) {
      navigateTo(`/lab/labRecords/${labRecordType}?status=${currentTab}`);
    } else {
      navigateTo(`/lab/labRecords/${labRecordType}/${item.id}?status=${currentTab}`);
    }
  };

  if (isMobile) {
    return (
      <Box sx={{ height, overflowY: "auto", overflowX: "hidden", width: "100%" }}>
        {labTestsFiltered.length === 0 ? (
          <Box textAlign="center" padding="50px">
            <Typography color="text.secondary">No items to show.</Typography>
          </Box>
        ) : (
          labTestsFiltered.map((item) => (
            <MobileLabCard
              key={item.id}
              item={item}
              isSelected={selectedItem?.id === item.id}
              onClick={() => handleRowClick(item)}
            />
          ))
        )}
      </Box>
    );
  }

  const desktopColumns = [
    {
      key: "testId",
      label: tl("lab.testId"),
      cellRenderer: ({ id, accessingResourceCentre, resourceCentre, labTestNumber }) => (
        <Typography component="div">
          {useCustomLabTestNumber ? labTestNumber || id : id}

          {accessingResourceCentre && (
            <Box>
              {accessingResourceCentre.id !== currentRCId ? (
                <Typography style={{ fontSize: "0.75rem", color: "grey" }}>
                  {accessingResourceCentre.name}
                </Typography>
              ) : (
                <Typography style={{ fontSize: "0.75rem", color: "grey" }}>
                  {resourceCentre.name}
                </Typography>
              )}
            </Box>
          )}
        </Typography>
      )
    },
    {
      key: "clientId",
      label: tl("lab.client"),
      cellRenderer: ({ client, customer }) => (
        <Typography
          component="div"
          title={
            customer?.externalIdentifier
              ? `${t("labPrint.customerIdentifier")}: ${customer.externalIdentifier}`
              : undefined
          }
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%"
            }}
          >
            {`${clientFullNameSelector(client)?.toUpperCase()} ${
              !client?.active ? "(DELETED)" : ""
            }`}
          </Box>

          <Box fontSize="small" color="gray" sx={{ whiteSpace: "nowrap" }}>
            {getCustomerNumber(customer?.customerNumber)}
            {customer?.externalIdentifier
              ? `(${t("labPrint.customerIdentifier")}: ${customer.externalIdentifier})`
              : ""}
          </Box>
        </Typography>
      ),
      sortable: false,
      sortFunction: (a, b) =>
        clientFullNameSelector(a.client) > clientFullNameSelector(b.client) ? 1 : -1
    },
    {
      key: "tests",
      label: tl("lab.tests"),
      cellRenderer: ({ results, related, emergencyId }) => {
        const allTests = [...(results?.data || [])].filter(Boolean);

        if (related?.length > 0) {
          related.forEach((rl) => {
            rl.results?.data?.forEach((resd) => allTests.push(resd));
          });
        }

        const filtered = allTests.filter(
          (v, i, a) => a.findIndex((item) => item.id === v.id) === i
        );

        if (!filtered.length) return null;

        const mainLabReading =
          filtered[0].labTests?.length > 0 &&
          filtered[0].labTests[0]?.subTests?.length > 0 &&
          hasOwnProperty(filtered[0].labTests[0], "formData") &&
          filtered[0].labTests[0].formData.reading;

        const readingColor = {
          Positive: "#FF6347",
          Negative: "#009654"
        };

        const testNamesString = filtered.map((lt, i) => {
          if (i < 2) return `${i ? ", " : ""}${lt.name}`;
          return null;
        });

        return (
          <Box sx={{ overflow: "hidden" }} title={testNamesString.join("")}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
              <Typography
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {testNamesString}
                {filtered.length > 2 ? "..." : ""}
              </Typography>

              {emergencyId && <EmergencyChip />}
            </Box>

            {mainLabReading && (
              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: readingColor[mainLabReading],
                  fontWeight: 600
                }}
              >
                {mainLabReading}
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      key: "createdAt",
      label: tl("lab.orderedOn"),
      // eslint-disable-next-line camelcase
      cellRenderer: ({ created_at }) => (
        <Box display="flex" flexDirection="column">
          <Typography>{calendarFns.bsFullDate(created_at)}</Typography>
          <Typography variant="subtitle1" color="textDisabled">
            {moment(created_at).tz("Asia/Kathmandu").format("hh:mm a")}
          </Typography>
        </Box>
      )
    },
    {
      key: "collectionDate",
      label: tl("lab.collected"),
      cellRenderer: ({ collectionDate }) => (
        <Box display="flex" flexDirection="column">
          <Typography>{collectionDate && calendarFns.bsFullDate(collectionDate)}</Typography>
          <Typography variant="subtitle1" color="textDisabled">
            {collectionDate && moment(collectionDate).tz("Asia/Kathmandu").format("hh:mm a")}
          </Typography>
        </Box>
      )
    },
    {
      key: "status",
      label: tl("lab.status"),
      cellRenderer: ({ status }) => (
        <Box display="flex" alignItems="center">
          <LabStatusChip status={status?.toLowerCase()} />
        </Box>
      ),
      sortable: false,
      sortFunction: (a, b) => (a > b ? 1 : -1)
    },
    {
      key: "turnAroundTime",
      label: tl("lab.turnAroundTime"),
      cellRenderer: (data) => <Box>{getTurnAroundTime(data)}</Box>
    }
  ];

  return (
    <AutoSizer disableHeight>
      {({ width }) => (
        <Box
          component="div"
          className={classNames("labRecordList", {
            withMultiSelect: Boolean(onMultiSelect)
          })}
          width={width - 1}
          height={height}
          overflow="auto"
        >
          <Box height="100%" minWidth="800px">
            <List
              automation="LabList"
              data={labTestsFiltered}
              rowHeight={50}
              defaultSortColumn="createdAt"
              adjustHeightToContents
              isResponsive
              multiSelectable={Boolean(onMultiSelect)}
              onMultiSelect={onMultiSelect}
              multiSelectContextHeader={multiSelectContextHeader}
              columns={desktopColumns}
              isLoading={isLoading}
              activeRow={selectedItem && selectedItem.id}
              onRowClick={handleRowClick}
            >
              <EmptyView>
                <Box textAlign="center" padding="50px">
                  No items to show.
                </Box>
              </EmptyView>
              <LoadingView>
                <ListLoadingView />
              </LoadingView>
              <ListActions>
                {() => (
                  <>
                    <Menu>
                      <Can policyAccessKey="lab:importCSV">
                        <CsvUploader
                          buttonText="Upload Lab Records (.csv)"
                          columns={tekuLabImportColumns}
                          requiredFieldInfoText="Note: Identifier, Patients name, Mobile number as well as PCR test result are mandatory information for importing."
                          createDataApi={createlabTestWithPCR}
                          runAfterSave={handleGetLabsTestRecords}
                          renderAdditionalInfo={() => (
                            <Typography
                              style={{
                                cursor: "pointer",
                                position: "fixed",
                                right: 20
                              }}
                            >
                              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                              <Link onClick={downloadLabUploadSample}>{tl("lab.viewSample")}</Link>
                            </Typography>
                          )}
                        />

                        <MenuItem onClick={downloadLabUploadSample}>
                          {tl("lab.viewSample")}
                        </MenuItem>
                      </Can>
                    </Menu>
                  </>
                )}
              </ListActions>
            </List>
          </Box>
        </Box>
      )}
    </AutoSizer>
  );
}

export default ListTab;
